import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Zoom from 'react-medium-image-zoom';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

interface LuxuryImageGalleryProps {
  images: string[];
  videos?: string[];
  alt?: string;
  onImageChange?: (index: number) => void;
}

const GalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
`;

const MainImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  background-color: ${props => props.theme.colors.background.secondary};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    aspect-ratio: 1;
  }
`;

const MainImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: zoom-in;
  transition: transform ${props => props.theme.transitions.slow};
  
  &:hover {
    transform: scale(1.02);
  }
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  overflow-x: auto;
  padding: ${props => props.theme.spacing.xs} 0;
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.colors.gray[400]} transparent;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.colors.gray[400]};
    border-radius: 2px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: ${props => props.theme.spacing.xs};
  }
`;

const Thumbnail = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  padding: 0;
  border: 2px solid ${props => 
    props.$active ? props.theme.colors.black : 'transparent'
  };
  background: none;
  cursor: pointer;
  overflow: hidden;
  transition: all ${props => props.theme.transitions.base};
  
  &:hover {
    border-color: ${props => props.theme.colors.gray[600]};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 60px;
    height: 60px;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform ${props => props.theme.transitions.base};
  
  ${Thumbnail}:hover & {
    transform: scale(1.1);
  }
`;

const ZoomHint = styled.div`
  position: absolute;
  bottom: ${props => props.theme.spacing.md};
  right: ${props => props.theme.spacing.md};
  background: rgba(0, 0, 0, 0.6);
  color: ${props => props.theme.colors.white};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.fontSize.sm};
  pointer-events: none;
  opacity: 0;
  transition: opacity ${props => props.theme.transitions.base};
  
  ${MainImageWrapper}:hover & {
    opacity: 1;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  background-color: ${props => props.theme.colors.background.secondary};
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MediaTypeIndicator = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.sm};
  right: ${props => props.theme.spacing.sm};
  background: rgba(0, 0, 0, 0.6);
  color: ${props => props.theme.colors.white};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  border-radius: ${props => props.theme.borderRadius.sm};
`;

const LuxuryImageGallery: React.FC<LuxuryImageGalleryProps> = ({
  images,
  videos = [],
  alt = 'Product image',
  onImageChange,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
  const [isPaused, setIsPaused] = useState(false);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Combine images and videos into a single media array
  const allMedia = [
    ...images.map(url => ({ type: 'image' as const, url })),
    ...videos.map(url => ({ type: 'video' as const, url }))
  ];

  // Reset selected index when images change
  const imagesKey = images.join(',');
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [imagesKey]);

  // Update parent when selection changes
  React.useEffect(() => {
    if (onImageChange && selectedIndex < allMedia.length) {
      onImageChange(selectedIndex);
    }
  }, [selectedIndex, onImageChange, allMedia.length]);

  // Update media type when selection changes
  React.useEffect(() => {
    if (selectedIndex < allMedia.length) {
      setMediaType(allMedia[selectedIndex].type);
    }
  }, [selectedIndex, allMedia]);

  // Auto-scroll functionality: advance to next image every 2 seconds
  useEffect(() => {
    // Clear any existing interval
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    // Only auto-scroll if:
    // - There are multiple media items
    // - Lightbox is not open
    // - Auto-scroll is not paused (user not hovering)
    if (allMedia.length > 1 && !lightboxOpen && !isPaused) {
      autoScrollIntervalRef.current = setInterval(() => {
        setSelectedIndex((prevIndex) => {
          // Loop back to 0 when reaching the end
          return (prevIndex + 1) % allMedia.length;
        });
      }, 2000); // 2 seconds
    }

    // Cleanup interval on unmount or when conditions change
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };
  }, [allMedia.length, lightboxOpen, isPaused]);

  // Pause auto-scroll when lightbox opens
  useEffect(() => {
    if (lightboxOpen && autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, [lightboxOpen]);

  if (allMedia.length === 0) {
    return (
      <GalleryContainer>
        <MainImageWrapper>
          <MainImage
            src="/placeholder.jpg"
            alt={alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        </MainImageWrapper>
      </GalleryContainer>
    );
  }

  const currentMedia = allMedia[selectedIndex];
  // Only include images in lightbox (videos handled separately)
  const imageMedia = allMedia.filter(media => media.type === 'image');
  const lightboxSlides = imageMedia.map((media) => ({
    src: media.url,
    alt: alt,
  }));
  
  // Find the index in the image-only array for lightbox
  const lightboxIndex = currentMedia.type === 'image' 
    ? imageMedia.findIndex(media => media.url === currentMedia.url)
    : 0;

  // Handle pause on hover
  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  return (
    <GalleryContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {currentMedia.type === 'image' && (
        <MainImageWrapper>
          <motion.div
            key={currentMedia.url}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            style={{ width: '100%', height: '100%', position: 'relative' }}
          >
            <Zoom>
              <MainImage
                src={currentMedia.url}
                alt={`${alt} ${selectedIndex + 1}`}
                onClick={() => setLightboxOpen(true)}
                style={{ opacity: 1 }}
              />
            </Zoom>
          </motion.div>
          <ZoomHint>Click to zoom</ZoomHint>
        </MainImageWrapper>
      )}
      {currentMedia.type === 'video' && (
        <VideoWrapper>
          <motion.div
            key={currentMedia.url}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            style={{ width: '100%', height: '100%' }}
          >
            <Video
              src={currentMedia.url}
              controls
              preload="metadata"
            />
          </motion.div>
          <MediaTypeIndicator>Video</MediaTypeIndicator>
        </VideoWrapper>
      )}

      {allMedia.length > 1 && (
        <ThumbnailContainer>
          {allMedia.map((media, index) => (
            <Thumbnail
              key={index}
              $active={index === selectedIndex}
              onClick={() => {
                setSelectedIndex(index);
                // Reset auto-scroll timer when user manually selects an image
                if (autoScrollIntervalRef.current) {
                  clearInterval(autoScrollIntervalRef.current);
                  autoScrollIntervalRef.current = null;
                }
                // Restart auto-scroll after a brief delay if not paused
                if (!lightboxOpen && !isPaused) {
                  setTimeout(() => {
                    if (allMedia.length > 1 && !lightboxOpen && !isPaused) {
                      autoScrollIntervalRef.current = setInterval(() => {
                        setSelectedIndex((prevIndex) => {
                          return (prevIndex + 1) % allMedia.length;
                        });
                      }, 2000);
                    }
                  }, 100);
                }
              }}
              aria-label={`View ${media.type} ${index + 1}`}
            >
              {media.type === 'image' ? (
                <ThumbnailImage src={media.url} alt={`${alt} thumbnail ${index + 1}`} />
              ) : (
                <>
                  <ThumbnailImage 
                    src={media.url} 
                    alt={`${alt} video thumbnail ${index + 1}`}
                    style={{ opacity: 0.7 }}
                  />
                  <MediaTypeIndicator style={{ position: 'absolute', bottom: '2px', right: '2px', fontSize: '10px', padding: '2px 4px' }}>
                    ▶
                  </MediaTypeIndicator>
                </>
              )}
            </Thumbnail>
          ))}
        </ThumbnailContainer>
      )}

      {currentMedia.type === 'image' && lightboxSlides.length > 0 && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex >= 0 ? lightboxIndex : 0}
          slides={lightboxSlides}
          controller={{ closeOnBackdropClick: true }}
          render={{
            buttonPrev: () => {
              const prevImageIndex = imageMedia.findIndex(media => media.url === currentMedia.url);
              const prevIndex = Math.max(0, prevImageIndex - 1);
              return (
                <button
                  onClick={() => {
                    if (prevIndex < imageMedia.length) {
                      setSelectedIndex(allMedia.findIndex(m => m.url === imageMedia[prevIndex].url));
                    }
                  }}
                  style={{
                    position: 'absolute',
                    left: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '24px',
                    zIndex: 1,
                  }}
                >
                  ‹
                </button>
              );
            },
            buttonNext: () => {
              const nextImageIndex = imageMedia.findIndex(media => media.url === currentMedia.url);
              const nextIndex = Math.min(imageMedia.length - 1, nextImageIndex + 1);
              return (
                <button
                  onClick={() => {
                    if (nextIndex < imageMedia.length) {
                      setSelectedIndex(allMedia.findIndex(m => m.url === imageMedia[nextIndex].url));
                    }
                  }}
                  style={{
                    position: 'absolute',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '24px',
                    zIndex: 1,
                  }}
                >
                  ›
                </button>
              );
            },
          }}
        />
      )}
    </GalleryContainer>
  );
};

export default LuxuryImageGallery;


